import {
  Alert,
  Button,
  Card,
  CardInset,
  FemaleIcon,
  Field,
  FieldHint,
  FilterChip,
  FilterChipRow,
  Input,
  Label,
  MaleIcon,
  Picker,
  ScrollRow,
  Textarea,
  cn,
} from "@breenova/ui";
import { ArrowLeft, ArrowRight, Camera, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DateField } from "@/components/date-field";
import { RingNumberField } from "@/components/ring-number-field";
import {
  useBird,
  useCreateBird,
  useLocations,
  useReferenceData,
  useUpdateBird,
} from "@/lib/birds";

interface FormState {
  ringNumber: string;
  ringYear: string;
  speciesId: string;
  hybridWithSpeciesId: string;
  sex: string;
  status: string;
  hatchDate: string;
  locationId: string;
  ringSizeId: string;
  federationId: string;
  origin: string;
  acquiredOn: string;
  acquiredFrom: string;
  notes: string;
  mutationIds: string[];
}

const EMPTY: FormState = {
  ringNumber: "",
  ringYear: String(new Date().getFullYear()),
  speciesId: "",
  hybridWithSpeciesId: "",
  sex: "Unknown",
  status: "InAviary",
  hatchDate: "",
  locationId: "",
  ringSizeId: "",
  federationId: "",
  origin: "BredHere",
  acquiredOn: "",
  acquiredFrom: "",
  notes: "",
  mutationIds: [],
};

/** One screen for both create and edit — the fields and rules are identical. */
export function BirdFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const reference = useReferenceData();
  const locations = useLocations(true);
  const existing = useBird(id);

  const create = useCreateBird();
  const update = useUpdateBird(id ?? "");

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  // Populated once the record arrives. Keyed on the bird's id rather than on the query
  // object so a background refetch cannot overwrite what the breeder is mid-way typing.
  useEffect(() => {
    const bird = existing.data;
    if (!bird) {
      return;
    }

    setForm({
      ringNumber: bird.ringNumber ?? "",
      ringYear: bird.ringYear?.toString() ?? "",
      speciesId: bird.speciesId,
      hybridWithSpeciesId: bird.hybridWithSpeciesId ?? "",
      sex: bird.sex,
      status: bird.status,
      hatchDate: bird.hatchDate ?? "",
      locationId: bird.locationId ?? "",
      ringSizeId: bird.ringSizeId ?? "",
      federationId: bird.federationId ?? "",
      origin: bird.origin,
      acquiredOn: bird.acquiredOn ?? "",
      acquiredFrom: bird.acquiredFrom ?? "",
      notes: bird.notes ?? "",
      mutationIds: bird.mutations.map((m) => m.mutationId),
    });
  }, [existing.data?.id]);

  const species = reference.data?.species ?? [];

  // Mutations are seeded per species, so the picker has to follow the species field —
  // offering a canary mutation for a goldfinch is a server-side rejection waiting to happen.
  const mutations = useMemo(() => {
    const all = reference.data?.mutations ?? [];
    const allowed = new Set([form.speciesId, form.hybridWithSpeciesId].filter(Boolean));

    return allowed.size === 0 ? [] : all.filter((m) => allowed.has(m.speciesId));
  }, [reference.data, form.speciesId, form.hybridWithSpeciesId]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleMutation(mutationId: string): void {
    setForm((current) => ({
      ...current,
      mutationIds: current.mutationIds.includes(mutationId)
        ? current.mutationIds.filter((existingId) => existingId !== mutationId)
        : [...current.mutationIds, mutationId],
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // Enter inside a field submits the form, so a step that is not the last one has to
    // advance rather than save a half-filled record.
    if (step < STEPS.length - 1) {
      if (step === 0 && !form.speciesId) {
        setError("Choose a species.");
        return;
      }

      setStep(step + 1);
      return;
    }

    if (!form.speciesId) {
      setStep(0);
      setError("Choose a species.");
      return;
    }

    const body = {
      ringNumber: form.ringNumber.trim() || null,
      ringYear: form.ringYear ? Number(form.ringYear) : null,
      speciesId: form.speciesId,
      hybridWithSpeciesId: form.hybridWithSpeciesId || null,
      sex: form.sex,
      status: form.status,
      hatchDate: form.hatchDate || null,
      locationId: form.locationId || null,
      ringSizeId: form.ringSizeId || null,
      federationId: form.federationId || null,
      origin: form.origin,
      acquiredOn: form.acquiredOn || null,
      acquiredFrom: form.acquiredFrom.trim() || null,
      acquiredPrice: null,
      acquiredCurrency: null,
      notes: form.notes.trim() || null,
      mutations: form.mutationIds.map((mutationId) => ({
        mutationId,
        isVisible: true,
        isConfirmed: true,
      })),
    };

    try {
      if (isEdit && id) {
        await update.mutateAsync({ ...body, deceasedDate: existing.data?.deceasedDate ?? null } as never);
        await navigate(`/birds/${id}`, { replace: true });
      } else {
        const created = await create.mutateAsync(body as never);
        await navigate(`/birds/${created?.id ?? ""}`, { replace: true });
      }
    } catch (caught) {
      // The API's domain rejections are specific — "NL-1234-25-007 is already on another
      // bird" — so they are shown verbatim rather than replaced with a generic failure.
      setError(caught instanceof Error ? caught.message : "Could not save the bird.");
    }
  }

  const saving = create.isPending || update.isPending;
  const last = step === STEPS.length - 1;

  function chooseSpecies(value: string): void {
    set("speciesId", value);
    // Mutations belong to a species; keeping them across a change would send rows
    // the server is bound to reject.
    set("mutationIds", []);
  }

  return (
    /*
     * Capped and centred from `md`.
     *
     * A form is one column of fields whatever the window is: a 1200px-wide text input is
     * harder to fill in than a 500px one, not easier, because the label at the left and
     * the cursor at the right stop being one glance apart. The wizard's step bar reads the
     * same way — three segments across a whole monitor say nothing three across a column
     * does not.
     */
    <div className="flex w-full flex-col md:mx-auto md:max-w-wide-form xl:max-w-none xl:flex-row xl:items-start xl:gap-xl">
      {/* The form keeps its capped column; the preview takes the width beside it. */}
      <div className="flex min-w-0 flex-col gap-4.5 xl:mx-0 xl:max-w-wide-form xl:flex-1">
      <header className="flex items-center gap-3 px-5 pt-3.5 md:px-gutter-wide md:pt-7.5">
        <Link
          to={isEdit && id ? `/birds/${id}` : "/birds"}
          aria-label="Cancel"
          className={cn(
            "flex size-touch shrink-0 items-center justify-center rounded-full",
            "bg-surface-container-lowest text-on-surface shadow-card",
          )}
        >
          <X className="size-5" />
        </Link>

        <span className="min-w-0 flex-1 truncate text-section-header-wide text-on-surface">
          {isEdit ? "Edit bird" : "New bird"}
        </span>

        <span className="shrink-0 text-node text-on-surface-variant" data-numeric>
          {step + 1} of {STEPS.length}
        </span>
      </header>

      <div className="flex flex-col gap-4.5 px-5 md:px-gutter-wide">
        {/*
         * Three segments rather than a single bar. A wizard's progress is countable —
         * "which of the three am I on" — and a continuous bar answers a question about
         * percentage that nobody asked.
         */}
        <ol className="flex items-center gap-1.5" aria-hidden>
          {STEPS.map((entry, index) => (
            <li
              key={entry.title}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                index <= step ? "bg-primary" : "bg-surface-container",
              )}
            />
          ))}
        </ol>

        <div className="flex flex-col gap-1">
          <h1 className="text-step-title text-on-surface">{STEPS[step]!.question}</h1>
          <p className="text-lead-wide text-on-surface-body">{STEPS[step]!.blurb}</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className={cn(
          "flex flex-col gap-4.5 px-5 md:px-gutter-wide",
          // Room for the floating footer, so the last field is never trapped under it.
          "pb-[calc(var(--spacing)*14+var(--spacing)*7.5+var(--spacing-lg))]",
        )}
      >
        {error ? <Alert tone="error">{error}</Alert> : null}

        {step === 0 ? (
          <>
            <div className="flex flex-col gap-4.5">
              <RingNumberField
                value={form.ringNumber}
                onChange={(value) => set("ringNumber", value)}
                year={form.ringYear ? Number(form.ringYear) : undefined}
              />

              {/*
               * Pills while the list is short, a picker once it is not.
               *
               * The design draws species as two pills, because its aviary keeps two.
               * The seeded reference data carries eleven, and eleven pills is four
               * wrapped rows that bury the rest of the step — so past four the choice
               * collapses into the same picker the cage uses.
               */}
              {species.length <= 4 ? (
                <ChoiceRow
                  label="Species"
                  fill={false}
                  value={form.speciesId}
                  onChange={chooseSpecies}
                  options={species.map((entry) => ({ value: entry.id, label: entry.name }))}
                />
              ) : (
                <Field>
                  <Label>Species</Label>
                  <Picker
                    id="speciesId"
                    label="Species"
                    placeholder="Choose a species…"
                    emptyLabel="No species available"
                    value={form.speciesId}
                    onChange={chooseSpecies}
                    options={species.map((entry) => ({ value: entry.id, label: entry.name }))}
                  />
                </Field>
              )}

              {/*
               * A segmented control, not a row of separate pills: three mutually
               * exclusive values that share one track, which is what the design
               * draws and what a radio group should be.
               */}
              <ChoiceRow
                label="Sex"
                fill
                value={form.sex}
                onChange={(value) => set("sex", value)}
                options={SEX_OPTIONS}
              />

              <Field>
                <Label>Cage</Label>
                <Picker
                  id="locationId"
                  label="Cage"
                  disabled={locations.isPending}
                  emptyLabel="No cages recorded yet"
                  value={form.locationId}
                  onChange={(value) => set("locationId", value)}
                  options={[
                    { value: "", label: "Not placed" },
                    ...(locations.data?.locations ?? []).map((location) => ({
                      value: location.id,
                      label: location.path,
                      ...(location.birdCount > 0
                        ? { hint: `${location.birdCount} birds` }
                        : {}),
                    })),
                  ]}
                />
              </Field>
            </div>

            {/* A prompt, not an uploader: photos are added from the bird's own screen
                once it exists and has an id to attach them to. */}
            <div className="flex items-center gap-3 rounded-card border-2 border-dashed border-surface-container-highest p-3.5">
              <span
                aria-hidden
                className="flex size-touch shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant"
              >
                <Camera className="size-5" />
              </span>
              <span className="flex min-w-0 flex-col gap-px">
                <span className="text-label text-on-surface">Add a photo</span>
                <span className="text-caption text-on-surface-variant">
                  Once the bird is saved — helps tell birds apart
                </span>
              </span>
            </div>
          </>
        ) : null}

        {step === 1 ? (
          <div className="flex flex-col gap-4.5">
            <Field>
              <Label htmlFor="ringYear">Ring year</Label>
              <Input
                id="ringYear"
                type="number"
                inputMode="numeric"
                min={1950}
                max={2100}
                data-numeric
                value={form.ringYear}
                onChange={(event) => set("ringYear", event.target.value)}
              />
            </Field>

            <Field>
              <Label>Hatch date</Label>
              <DateField
                id="hatchDate"
                label="Hatch date"
                placeholder="Not recorded"
                value={form.hatchDate}
                onChange={(next) => set("hatchDate", next)}
              />
            </Field>

            <Field>
              <Label>Crossed with</Label>
              <Picker
                id="hybridWithSpeciesId"
                label="Crossed with"
                emptyLabel="No other species to cross with"
                value={form.hybridWithSpeciesId}
                onChange={(value) => set("hybridWithSpeciesId", value)}
                options={[
                  { value: "", label: "Not a mule" },
                  ...species
                    .filter((entry) => entry.id !== form.speciesId)
                    .map((entry) => ({ value: entry.id, label: entry.name })),
                ]}
              />
              <FieldHint>For a goldfinch × canary mule, pick the other side here.</FieldHint>
            </Field>

            {mutations.length > 0 ? (
              /*
               * One scrolling line rather than a wrapping block.
               *
               * A species can carry a couple of dozen mutations, and wrapped they took
               * four rows and pushed the rest of the step off the screen. A dropdown is
               * the wrong shape for the opposite reason — a bird carries several at
               * once, and a single-choice menu cannot say so. The chip row is the same
               * control the Birds and Nests screens already scroll sideways.
               */
              <div className="flex flex-col gap-sm">
                <Label>
                  Mutations
                  {form.mutationIds.length > 0 ? ` · ${form.mutationIds.length} chosen` : ""}
                </Label>

                <FilterChipRow aria-label="Mutations">
                  {mutations.map((mutation) => (
                    <FilterChip
                      key={mutation.id}
                      active={form.mutationIds.includes(mutation.id)}
                      onClick={() => toggleMutation(mutation.id)}
                    >
                      {mutation.name}
                    </FilterChip>
                  ))}
                </FilterChipRow>

                <FieldHint>
                  Tap each one the bird carries. Swipe the row for the rest.
                </FieldHint>
              </div>
            ) : null}

            <FieldHint>
              Sire and dam are set from the bird&rsquo;s own screen once it exists — the
              parentage rules need a bird to check the hatch order against.
            </FieldHint>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="flex flex-col gap-4.5">
            <ChoiceRow
              label="Origin"
              value={form.origin}
              onChange={(value) => set("origin", value)}
              options={[
                { value: "BredHere", label: "Bred here" },
                { value: "Purchased", label: "Purchased" },
                { value: "Gifted", label: "Gifted" },
                { value: "Imported", label: "Imported" },
                { value: "OnLoan", label: "On loan" },
              ]}
            />

            {form.origin !== "BredHere" ? (
              <>
                <Field>
                  <Label>Acquired on</Label>
                  <DateField
                    id="acquiredOn"
                    label="Acquired on"
                    placeholder="Not recorded"
                    value={form.acquiredOn}
                    onChange={(next) => set("acquiredOn", next)}
                  />
                </Field>

                <Field>
                  <Label htmlFor="acquiredFrom">Acquired from</Label>
                  <Input
                    id="acquiredFrom"
                    value={form.acquiredFrom}
                    onChange={(event) => set("acquiredFrom", event.target.value)}
                  />
                </Field>
              </>
            ) : null}

            <Field>
              <Label>Status</Label>
              <Picker
                id="status"
                label="Status"
                value={form.status}
                onChange={(value) => set("status", value)}
                options={STATUS_OPTIONS}
              />
              <FieldHint>
                Marking a bird sold, loaned, escaped or dead empties its cage and records
                the move.
              </FieldHint>
            </Field>

            <Field>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={4}
                placeholder="Song, condition, show results…"
                value={form.notes}
                onChange={(event) => set("notes", event.target.value)}
              />
            </Field>
          </div>
        ) : null}

        {/* Floating rather than pinned to the end of the form: on a three-step wizard the
            way forward should never be something you have to scroll to find. */}
        <div
          className={cn(
            "absolute inset-x-0 z-10 mx-auto flex max-w-content items-center gap-md px-5",
            /* The tab bar steps aside on this screen, so the footer sits on the bottom edge. */
            "bottom-[calc(var(--spacing)*7.5+var(--safe-area-bottom))] gap-2.5",
            /*
             * On a desktop it clears the rail and sits above the window's own bottom
             * edge rather than above a tab bar that is not there. The right
             * inset matches the left so the bar stays under the capped form column
             * instead of running the full width of the monitor beneath it.
             */
            "md:bottom-xl md:pl-[calc(var(--container-sidebar)+var(--spacing-gutter-wide))] md:pr-gutter-wide",
          )}
        >
          <button
            type="button"
            onClick={() => (step === 0 ? void navigate(-1) : setStep(step - 1))}
            aria-label={step === 0 ? "Cancel" : "Back a step"}
            className={cn(
              "flex size-14 shrink-0 items-center justify-center rounded-full",
              "bg-surface-container-lowest text-on-surface shadow-raised",
            )}
          >
            <ArrowLeft className="size-5.25" />
          </button>

          <Button type="submit" size="lg" block disabled={saving} className="shadow-raised">
            {saving
              ? "Saving…"
              : last
                ? isEdit
                  ? "Save changes"
                  : "Add bird"
                : `Next: ${STEPS[step + 1]!.title.toLowerCase()}`}
            {last ? null : <ArrowRight className="size-5" />}
          </Button>
        </div>
      </form>
      </div>

      <BirdPreview
        form={form}
        speciesName={species.find((entry) => entry.id === form.speciesId)?.name}
        mutationNames={mutations
          .filter((mutation) => form.mutationIds.includes(mutation.id))
          .map((mutation) => mutation.name)}
        cageCode={
          locations.data?.locations.find((location) => location.id === form.locationId)?.code
        }
      />
    </div>
  );
}

/**
 * The record as it will be filed, beside the form that is filling it in.
 *
 * From `xl` only, and it adds nothing the fields do not already say — that is the point.
 * A wizard hides two thirds of what it is building behind its own steps, and a breeder on
 * step three has no way to check the ring number they typed on step one without going
 * back and losing their place. This is that check.
 *
 * No photograph slot. The form does not upload one — a photo is added from the bird's own
 * screen once it exists — and a drop zone that only says "later" is worse than no slot.
 */
function BirdPreview({
  form,
  speciesName,
  mutationNames,
  cageCode,
}: {
  form: FormState;
  speciesName: string | undefined;
  mutationNames: readonly string[];
  cageCode: string | undefined;
}) {
  const sex = SEX_OPTIONS.find((option) => option.value === form.sex);


  return (
    <aside className="hidden w-[var(--container-flank)] shrink-0 flex-col gap-md pr-2xl pt-xl xl:flex">
      <span className="text-label-caps uppercase text-on-surface-variant">Preview</span>

      <Card className="flex flex-col gap-md p-lg">
        <div className="flex flex-col gap-hair">
          <span className="truncate text-label-caps uppercase text-on-surface-variant">
            {[speciesName, ...mutationNames].filter(Boolean).join(" · ") || "No species yet"}
          </span>
          <span className="truncate text-card-title text-on-surface" data-numeric>
            {form.ringNumber || "Unringed"}
          </span>
        </div>

        <CardInset className="flex justify-between gap-sm">
          <PreviewFact label="Sex" value={sex ? sex.word : "Unknown"} />
          <PreviewFact label="Hatched" value={form.hatchDate || "—"} />
          <PreviewFact label="Cage" value={cageCode ?? "—"} />
        </CardInset>
      </Card>
    </aside>
  );
}

function PreviewFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-px">
      <span className="text-tile-label uppercase text-on-surface-variant">{label}</span>
      <span className="truncate text-data text-on-surface" data-numeric>
        {value}
      </span>
    </div>
  );
}

/** Where a bird stands. Leaving the aviary empties its cage and records the move. */
const STATUS_OPTIONS = [
  { value: "InAviary", label: "In aviary" },
  { value: "Quarantined", label: "Quarantined" },
  { value: "Sold", label: "Sold" },
  { value: "Loaned", label: "Loaned out" },
  { value: "Escaped", label: "Escaped" },
  { value: "Deceased", label: "Deceased" },
];

/**
 * The three values a bird's sex can take, as the design words them.
 *
 * Drawn marks rather than the Unicode glyphs that used to be part of the label. Typed,
 * they took the label's own font size and the font's own vertical centring, so they
 * arrived at about nine pixels and sat low against the word beside them; as icons they
 * are sized and aligned by the control like every other icon in the app.
 */
const SEX_OPTIONS = [
  {
    value: "Male",
    label: (
      <span className="flex items-center gap-1.5">
        Cock <MaleIcon className="size-3.75 shrink-0" />
      </span>
    ),
    word: "Cock",
  },
  {
    value: "Female",
    label: (
      <span className="flex items-center gap-1.5">
        Hen <FemaleIcon className="size-3.75 shrink-0" />
      </span>
    ),
    word: "Hen",
  },
  { value: "Unknown", label: "Not sure", word: "Unknown" },
] as const;

/**
 * The three steps, in the order a breeder actually knows the answers.
 *
 * Identity first because a ring number and a species are all the record needs to exist;
 * everything after it can be filled in with the bird back in its cage.
 */
const STEPS: readonly { title: string; question: string; blurb: string }[] = [
  {
    title: "Identity",
    question: "Who is it?",
    blurb: "A ring number and a species is enough. Pedigree and origin come next.",
  },
  {
    title: "Pedigree",
    question: "What does it carry?",
    blurb: "Ring year, hatch date and its mutations. All optional.",
  },
  {
    title: "Provenance",
    question: "Where is it from?",
    blurb: "Where it came from, where it stands now, and anything worth remembering.",
  },
];

/**
 * A short set of options as a row of pills rather than a `<select>`.
 *
 * Worth the width for two to five choices: the answer is visible without opening
 * anything, and on a phone it is one tap instead of a platform picker and a confirm.
 * Anything longer stays a native select, which scrolls properly and searches by keystroke.
 */
function ChoiceRow({
  label,
  value,
  onChange,
  options,
  fill = options.length <= 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: ReactNode }[];
  /**
   * Share the row evenly, which reads as one control — right for three answers to one
   * question. Off, each pill is as wide as its word and the row scrolls sideways, which
   * is right for a list of names.
   */
  fill?: boolean;
}) {
  const share = fill;

  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>

      <ScrollRow aria-label={label} className={cn("gap-2 pb-0", share ? "overflow-x-visible" : "")}>
        {options.map((option) => {
          const selected = option.value === value;

          return (
            <li key={option.value} className={share ? "min-w-0 flex-1" : "shrink-0"}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onChange(option.value)}
                className={cn(
                  "flex h-11 w-full items-center justify-center rounded-control px-4.5",
                  "text-label transition-colors",
                  selected
                    ? "bg-primary-strong text-on-primary-strong"
                    : "bg-surface-container text-on-surface hover:bg-surface-container-high",
                )}
              >
                {option.label}
              </button>
            </li>
          );
        })}
      </ScrollRow>
    </div>
  );
}
